import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MapGL, { Marker, NavigationControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { CATEGORIES } from '@/components/NeedNowGrid';
import { useCity } from '@/context/CityContext';
import { getPlaces, getTransit, isAbortedRequestError } from '@/lib/api';
import { ArrowLeft, Loader2, Navigation, Star, LocateFixed, Train, Bus, Accessibility, Radio } from 'lucide-react';
import { Stethoscope, Pill, Siren, Coffee, Hotel, Bath } from 'lucide-react';
import { toast } from 'sonner';

const CAT_ICON = { clinic: Stethoscope, pharmacy: Pill, emergency: Siren, cafe: Coffee, hotel: Hotel, washroom: Bath, transit: Train };
// Map-screen specific category list (adds Transit on top of the Need-Now categories)
const MAP_CATEGORIES = [...CATEGORIES, { key: 'transit', label: 'Transit', icon: Train }];

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAP_STYLE = 'mapbox://styles/mapbox/light-v11';

function PuvioMarker({ category, selected, onClick }) {
  const Icon = CAT_ICON[category] || Stethoscope;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`puvio-pin transition-transform ${selected ? 'scale-125 z-30' : 'z-10'}`}
      aria-label="Place marker"
    >
      <span className="puvio-pin-inner"><Icon size={16} /></span>
    </button>
  );
}

/**
 * Transit-specific marker. Uses the route's brand color and shows the route's
 * short_name (or transit mode glyph) so users can spot the line on the map.
 */
function TransitMarker({ route, selected, onClick }) {
  const isBus = (route.mode || '').toLowerCase().includes('bus');
  const Glyph = isBus ? Bus : Train;
  const bg = route.color || '#1F4480';
  const fg = route.text_color || '#FFFFFF';
  const short = (route.short_name || '').slice(0, 3);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`relative transition-transform ${selected ? 'scale-125 z-30' : 'z-10'}`}
      aria-label={`Transit ${route.short_name}`}
    >
      <div
        className="min-w-[32px] h-8 px-1.5 rounded-full flex items-center justify-center gap-1 font-extrabold text-[11px] tracking-tight shadow-[0_6px_16px_rgba(7,22,52,0.25)] border-[2px] border-white"
        style={{ background: bg, color: fg }}
      >
        <Glyph size={12} />
        <span>{short}</span>
      </div>
    </button>
  );
}

function UserDot() {
  return (
    <div className="w-5 h-5 rounded-full bg-[#2F80ED] border-[3px] border-white shadow-[0_0_0_4px_rgba(47,128,237,0.25)]" />
  );
}

export default function MapScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialCat = params.get('c') || 'pharmacy';
  const [cat, setCat] = useState(initialCat);
  const { city } = useCity();
  const [items, setItems] = useState([]);
  const [transitData, setTransitData] = useState(null); // {covered, routes:[]}
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const mapRef = useRef(null);

  const isTransit = cat === 'transit';

  const [viewState, setViewState] = useState({
    latitude: city.lat, longitude: city.lon, zoom: 13.5,
  });

  useEffect(() => {
    setViewState((s) => ({ ...s, latitude: city.lat, longitude: city.lon }));
    if (mapRef.current) mapRef.current.flyTo({ center: [city.lon, city.lat], zoom: 13.5, duration: 900 });
  }, [city.lat, city.lon]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setSelectedId(null); setItems([]); setTransitData(null);
    if (isTransit) {
      // Fetch real-time transit routes & stops via Transit (transitapp.com)
      (async () => {
        let data = null;
        try {
          data = await getTransit(city.lat, city.lon, 1500);
        } catch (err) {
          if (!alive) return;
          if (!isAbortedRequestError(err)) {
            // eslint-disable-next-line no-console
            console.warn('[transit] fetch failed', err);
            toast.error('Transit failed to load');
          }
          setTransitData({ covered: false, routes: [] });
          setItems([]);
          setLoading(false);
          return;
        }
        if (!alive) return;
        // Defensive transformation — never throw out of this block.
        const stopItems = [];
        try {
          const byStop = new Map();
          for (const r of (data?.routes || [])) {
            const stop = r?.stop || {};
            if (typeof stop.lat !== 'number' || typeof stop.lon !== 'number') continue;
            const sid = stop.global_stop_id || `${stop.lat},${stop.lon}`;
            if (!byStop.has(sid)) {
              byStop.set(sid, {
                id: sid,
                name: stop.name || 'Stop',
                lat: stop.lat,
                lon: stop.lon,
                distance_m: stop.distance_m,
                wheelchair: stop.wheelchair_boarding === 1,
                routes: [],
              });
            }
            byStop.get(sid).routes.push(r);
          }
          for (const s of byStop.values()) stopItems.push(s);
          stopItems.sort((a, b) => (a.distance_m ?? 9999) - (b.distance_m ?? 9999));
        } catch (transformErr) {
          // eslint-disable-next-line no-console
          console.warn('[transit] transform failed', transformErr);
        }
        if (!alive) return;
        setTransitData(data || { covered: false, routes: [] });
        setItems(stopItems);
        setLoading(false);
      })();
    } else {
      getPlaces(city.lat, city.lon, cat, 3000, 30)
        .then((r) => { if (alive) { setItems(r.items || []); } })
        .catch((err) => { if (alive && !isAbortedRequestError(err)) { toast.error('Failed to load places'); setItems([]); } })
        .finally(() => alive && setLoading(false));
    }
    return () => { alive = false; };
  }, [city.lat, city.lon, cat, isTransit]);

  const selected = useMemo(() => items.find((p) => p.id === selectedId), [items, selectedId]);

  const focusPlace = (p) => {
    setSelectedId(p.id);
    if (mapRef.current) mapRef.current.flyTo({ center: [p.lon, p.lat], zoom: 15.5, duration: 600 });
  };

  if (!MAPBOX_TOKEN) {
    return (
      <div className="p-8 text-center text-sm text-[#4A5A50]" data-testid="map-unavailable">
        Map temporarily unavailable. Please try again later.
      </div>
    );
  }

  // Pick the "primary" route for a given stop (soonest departure)
  const primaryRoute = (stop) => {
    if (!stop?.routes || stop.routes.length === 0) return null;
    const sorted = [...stop.routes].sort((a, b) => (a.next_minutes ?? 9999) - (b.next_minutes ?? 9999));
    return sorted[0];
  };

  return (
    <div className="relative" data-testid="map-screen">
      {/* Floating top bar */}
      <div className="absolute inset-x-0 top-0 z-20 pointer-events-none">
        <div className="px-4 pt-4 pb-2 flex items-center gap-2 pointer-events-auto">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white border border-black/10 flex items-center justify-center shadow-sm puvio-focus" aria-label="Back" data-testid="map-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 puvio-pill puvio-pill-light shadow-sm">
            <Navigation className="w-3.5 h-3.5 text-[#15291C]" />
            <span className="truncate">{city.name}{city.country ? `, ${city.country}` : ''}</span>
          </div>
        </div>
        <div className="px-4 pb-2 flex items-center gap-2 overflow-x-auto no-scrollbar pointer-events-auto" data-testid="map-category-chips">
          {MAP_CATEGORIES.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)} className={`puvio-pill puvio-focus shrink-0 ${cat === c.key ? 'puvio-pill-accent' : 'bg-white text-[#0F1A13] shadow-sm border border-black/5'}`} data-testid={`map-chip-${c.key}`}>
              <c.icon className="w-3.5 h-3.5" /> {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-dvh md:h-[calc(100dvh-48px)]">
        <MapGL
          ref={mapRef}
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle={MAP_STYLE}
          {...viewState}
          onMove={(evt) => setViewState(evt.viewState)}
          attributionControl={false}
          style={{ width: '100%', height: '100%' }}
        >
          {/* User location */}
          <Marker longitude={city.lon} latitude={city.lat} anchor="center">
            <UserDot />
          </Marker>
          {/* Place / transit-stop markers */}
          {items.map((p) => (
            <Marker key={p.id} longitude={p.lon} latitude={p.lat} anchor="bottom">
              {isTransit ? (
                <TransitMarker route={primaryRoute(p) || { short_name: '•', mode: 'Transit', color: '#1F4480', text_color: '#FFFFFF' }} selected={p.id === selectedId} onClick={() => focusPlace(p)} />
              ) : (
                <PuvioMarker category={cat} selected={p.id === selectedId} onClick={() => focusPlace(p)} />
              )}
            </Marker>
          ))}

          <NavigationControl position="bottom-right" showCompass={false} style={{ marginRight: 12, marginBottom: 240 }} />
        </MapGL>
      </div>

      {/* Recenter pill */}
      <button
        type="button"
        onClick={() => mapRef.current && mapRef.current.flyTo({ center: [city.lon, city.lat], zoom: 13.5 })}
        className="absolute top-32 right-3 z-10 w-10 h-10 rounded-full bg-[#C4FF3D] shadow-md flex items-center justify-center hover:brightness-95 transition-all puvio-focus"
        aria-label="Recenter"
        data-testid="map-recenter"
      >
        <LocateFixed className="w-4 h-4 text-[#0F1A13]" />
      </button>

      {loading && (
        <div className="absolute top-32 left-1/2 -translate-x-1/2 bg-white shadow-md rounded-full px-3 py-1.5 text-xs flex items-center gap-2 z-10">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading {cat}…
        </div>
      )}

      {/* Transit-not-covered banner */}
      {!loading && isTransit && transitData && !transitData.covered && (
        <div className="absolute top-32 left-3 right-16 z-10 puvio-card p-3" data-testid="transit-not-covered">
          <p className="text-sm font-semibold text-[#0F1A13]">
            {transitData.error === 'rate_limited' ? 'Transit busy — try again' : 'Transit not available here'}
          </p>
          <p className="text-xs text-[var(--puvio-text-2)] mt-0.5">
            {transitData.error === 'rate_limited'
              ? 'Real-time transit is temporarily rate-limited. Tap a different chip and come back in about a minute.'
              : `Real-time public transit data isn't available for ${city.name} yet. Coverage is strongest across North America, the UK, and parts of Europe & Australia.`}
          </p>
        </div>
      )}

      {/* Selected place mini-popup */}
      {selected && !isTransit && (
        <div
          className="absolute top-32 left-3 right-16 z-10 puvio-card p-3 flex items-center gap-3 cursor-pointer"
          onClick={() => setSelectedId(null)}
          data-testid="map-selected-popup"
        >
          {selected.photo_url ? (
            <img src={selected.photo_url} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" loading="lazy" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-[#C4FF3D]/30 flex items-center justify-center shrink-0">
              {(() => { const I = CAT_ICON[cat] || Stethoscope; return <I className="w-5 h-5 text-[#15291C]" />; })()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{selected.name}</p>
            <p className="text-xs text-[#4A5A50] truncate">
              {selected.rating != null && (<><Star className="inline w-3 h-3 text-[#FFB020] fill-[#FFB020] mr-0.5" />{selected.rating} · </>)}
              {Math.round(selected.distance_m)} m{selected.address ? ` · ${selected.address}` : ''}
            </p>
          </div>
          <a href={selected.google_maps_uri || `https://www.google.com/maps/dir/?api=1&destination=${selected.lat},${selected.lon}&origin=${city.lat},${city.lon}`} target="_blank" rel="noreferrer" className="puvio-pill puvio-pill-accent text-xs shrink-0" onClick={(e) => e.stopPropagation()} data-testid="map-popup-go">
            Go
          </a>
        </div>
      )}

      {/* Transit selected stop popup: shows up to 3 routes with next departures */}
      {selected && isTransit && (
        <div
          className="absolute top-32 left-3 right-16 z-10 puvio-card p-3 cursor-pointer"
          onClick={() => setSelectedId(null)}
          data-testid="transit-selected-popup"
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate text-[#0F1A13]">{selected.name}</p>
              <p className="text-[11px] text-[var(--puvio-text-2)] mt-0.5">
                {Math.round(selected.distance_m || 0)} m away · {selected.routes?.length || 0} route{(selected.routes?.length || 0) !== 1 ? 's' : ''}
                {selected.wheelchair && (<span className="inline-flex items-center gap-1 ml-1 text-[var(--puvio-mint-deep)]"><Accessibility className="w-3 h-3" /></span>)}
              </p>
            </div>
          </div>
          <ul className="mt-2 flex flex-col gap-1.5">
            {(selected.routes || []).slice(0, 3).map((r) => (
              <li key={r.global_route_id} className="flex items-center gap-2">
                <div
                  className="shrink-0 min-w-[36px] h-7 rounded-lg px-1.5 flex items-center justify-center font-extrabold text-[11px] tracking-tight"
                  style={{ background: r.color || '#1F4480', color: r.text_color || '#FFFFFF' }}
                >{r.short_name}</div>
                <p className="text-[12px] text-[var(--puvio-text-2)] flex-1 truncate">{r.headsign || r.long_name}</p>
                <span className={`text-[12px] font-bold ${r.next_minutes != null && r.next_minutes <= 1 ? 'text-[var(--puvio-coral-deep)]' : 'text-[#0F1A13]'}`}>
                  {r.next_minutes == null ? '—' : (r.next_minutes <= 0 ? 'Now' : `${r.next_minutes} min`)}
                </span>
                {r.is_real_time && (
                  <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-[var(--puvio-mint-deep)]">
                    <Radio className="w-2.5 h-2.5" /> live
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bottom carousel */}
      {!loading && (
        <div className="absolute bottom-24 left-0 right-0 px-3 z-10">
          <div className="flex gap-3 overflow-x-auto no-scrollbar snap-x-mandatory pb-1" data-testid="map-place-carousel">
            {!isTransit && items.slice(0, 20).map((p, i) => (
              <article
                key={p.id}
                onClick={() => focusPlace(p)}
                className={`puvio-card p-0 min-w-[260px] snap-start cursor-pointer puvio-card-interactive overflow-hidden ${selectedId === p.id ? 'ring-2 ring-[#C4FF3D]' : ''}`}
                data-testid={`map-card-${i}`}
              >
                {p.photo_url && (
                  <img src={p.photo_url} alt={p.name} className="w-full h-24 object-cover" loading="lazy" referrerPolicy="no-referrer" />
                )}
                <div className="p-3">
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-[#4A5A50] mt-0.5 flex items-center gap-1.5">
                    {p.rating != null && (<><Star className="w-3 h-3 text-[#FFB020] fill-[#FFB020]" /> {p.rating} {p.rating_count ? `(${p.rating_count})` : ''} ·</>)}
                    {Math.round(p.distance_m)} m
                  </p>
                  {p.open_now != null && (
                    <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ background: p.open_now ? 'rgba(46,212,122,0.15)' : 'rgba(255,77,79,0.12)', color: p.open_now ? '#2ED47A' : '#FF4D4F' }}>
                      {p.open_now ? 'Open now' : 'Closed'}
                    </span>
                  )}
                  <a href={p.google_maps_uri || `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lon}&origin=${city.lat},${city.lon}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex puvio-pill puvio-pill-accent text-xs" onClick={(e) => e.stopPropagation()} data-testid={`map-card-directions-${i}`}>
                    Directions
                  </a>
                </div>
              </article>
            ))}
            {isTransit && items.slice(0, 20).map((p, i) => {
              const top = primaryRoute(p);
              return (
                <article
                  key={p.id}
                  onClick={() => focusPlace(p)}
                  className={`puvio-card p-3 min-w-[260px] snap-start cursor-pointer puvio-card-interactive ${selectedId === p.id ? 'ring-2 ring-[#C4FF3D]' : ''}`}
                  data-testid={`map-transit-card-${i}`}
                >
                  <div className="flex items-center gap-2">
                    {top && (
                      <div
                        className="shrink-0 min-w-[36px] h-8 rounded-lg px-1.5 flex items-center justify-center font-extrabold text-xs"
                        style={{ background: top.color || '#1F4480', color: top.text_color || '#FFFFFF' }}
                      >{top.short_name}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate text-[#0F1A13]">{p.name}</p>
                      <p className="text-[11px] text-[var(--puvio-text-2)] flex items-center gap-1.5 mt-0.5 font-medium">
                        {p.distance_m != null ? `${Math.round(p.distance_m)} m` : ''}
                        <span>·</span>
                        <span>{p.routes?.length || 0} route{(p.routes?.length || 0) !== 1 ? 's' : ''}</span>
                        {p.wheelchair && <Accessibility className="w-3 h-3 text-[var(--puvio-mint-deep)]" />}
                      </p>
                    </div>
                  </div>
                  {top && (
                    <div className="mt-2 flex items-center gap-2 text-[12px]">
                      <span className="text-[var(--puvio-text-2)] truncate flex-1">{top.headsign || top.long_name || top.mode}</span>
                      <span className={`font-bold ${top.next_minutes != null && top.next_minutes <= 1 ? 'text-[var(--puvio-coral-deep)]' : 'text-[#0F1A13]'}`}>
                        {top.next_minutes == null ? '—' : (top.next_minutes <= 0 ? 'Now' : `${top.next_minutes} min`)}
                      </span>
                      {top.is_real_time && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[var(--puvio-mint-deep)]">
                          <Radio className="w-2.5 h-2.5" /> live
                        </span>
                      )}
                    </div>
                  )}
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lon}&origin=${city.lat},${city.lon}&travelmode=walking`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex puvio-pill puvio-pill-accent text-xs"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`map-transit-card-directions-${i}`}
                  >
                    Walk to stop
                  </a>
                </article>
              );
            })}
            {items.length === 0 && !loading && !(isTransit && transitData && !transitData.covered) && (
              <div className="puvio-card p-4 text-sm text-[#4A5A50]">No {cat} found within {isTransit ? '1.5 km' : '3 km'}.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
